import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState, useRef } from "react";
import { subscribeToLoading } from "@/config/api_interceptor";
import { subscribeToApiClientLoading } from "@/core/api/apiClient";

export const useGlobalLoading = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiClientLoading, setApiClientLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const queryClient = useQueryClient();
  const prevLoadingRef = useRef(false);
  const apiLoadingRef = useRef(false);
  const apiClientLoadingRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 상태 변경 시에만 setState
  const checkLoadingState = useCallback(() => {
    const queries = queryClient.getQueryCache().getAll();
    const mutations = queryClient.getMutationCache().getAll();

    // status === "pending"은 "데이터 없음" 상태로 실제 요청 중이 아닐 수 있음
    // fetchStatus === "fetching"이 실제 네트워크 요청 중인 상태
    const hasLoadingQueries = queries.some(
      (query) => query.state.fetchStatus === "fetching"
    );
    const hasLoadingMutations = mutations.some(
      (mutation) => mutation.state.status === "pending"
    );

    const queryLoading = hasLoadingQueries || hasLoadingMutations;
    const newLoadingState =
      queryLoading || apiLoadingRef.current || apiClientLoadingRef.current;

    if (prevLoadingRef.current !== newLoadingState) {
      prevLoadingRef.current = newLoadingState;
      setIsLoading(newLoadingState);
    }
  }, [queryClient]);

  useEffect(() => {
    // API 인터셉터 로딩 상태 구독
    const unsubscribeApi = subscribeToLoading((loading) => {
      apiLoadingRef.current = loading;
      checkLoadingState();
    });

    // API 클라이언트 로딩 상태 구독
    const unsubscribeApiClient = subscribeToApiClientLoading((loading) => {
      apiClientLoadingRef.current = loading;
      checkLoadingState();
    });

    // 초기 로딩 상태 체크
    checkLoadingState();

    // 쿼리 상태 변경 감지
    const unsubscribeQueries = queryClient.getQueryCache().subscribe(() => {
      setTimeout(checkLoadingState, 0);
    });

    // 뮤테이션 상태 변경 감지
    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe(() => {
        setTimeout(checkLoadingState, 0);
      });

    return () => {
      unsubscribeApi();
      unsubscribeApiClient();
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, checkLoadingState]);

  // 1초 이상 로딩이 지속될 때만 showLoader를 true로
  useEffect(() => {
    if (isLoading) {
      timerRef.current = setTimeout(() => setShowLoader(true), 1000);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      setShowLoader(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLoading]);

  return showLoader;
};
